import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 스트리밍 텍스트 버퍼 훅 — 결정 #14.
 *
 * 토큰마다 setState하면 6s+에서 잰크가 생긴다. 대신 들어오는 델타를 ref 버퍼에
 * 누적하고 requestAnimationFrame으로 프레임당 1회만 화면을 갱신한다. 생성이
 * 끝나면 마지막 flush 후 버퍼를 비운다.
 *
 * 추론(reasoning) 채널도 같은 ref+rAF 패턴으로 분리 추적한다(o1/Claude thinking
 * 등 추론 모델의 사고 과정을 본문과 별도로 표시하기 위함).
 *
 * 반환:
 *  - text / reasoning: 현재까지 화면에 보여줄 값(프레임 배치된 값).
 *  - streaming: 진행 중 여부.
 *  - begin/append/appendReasoning/end: 오케스트레이터가 호출하는 제어 함수.
 */
export function useStreamingBuffer() {
  const [text, setText] = useState("");
  const [reasoning, setReasoning] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bufferRef = useRef("");
  const reasoningRef = useRef("");
  const rafRef = useRef<number | null>(null);
  const dirtyRef = useRef(false);
  const reasoningDirtyRef = useRef(false);

  const flush = useCallback(() => {
    rafRef.current = null;
    if (dirtyRef.current) {
      dirtyRef.current = false;
      setText(bufferRef.current);
    }
    if (reasoningDirtyRef.current) {
      reasoningDirtyRef.current = false;
      setReasoning(reasoningRef.current);
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(flush);
  }, [flush]);

  const begin = useCallback(() => {
    bufferRef.current = "";
    reasoningRef.current = "";
    dirtyRef.current = false;
    reasoningDirtyRef.current = false;
    setText("");
    setReasoning("");
    setStreaming(true);
  }, []);

  const append = useCallback(
    (delta: string) => {
      bufferRef.current += delta;
      dirtyRef.current = true;
      scheduleFlush();
    },
    [scheduleFlush],
  );

  const appendReasoning = useCallback(
    (delta: string) => {
      reasoningRef.current += delta;
      reasoningDirtyRef.current = true;
      scheduleFlush();
    },
    [scheduleFlush],
  );

  const end = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    // 마지막 한 번 더 동기 flush로 누락 방지.
    setText(bufferRef.current);
    setReasoning(reasoningRef.current);
    setStreaming(false);
  }, []);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return { text, reasoning, streaming, begin, append, appendReasoning, end };
}
